// OrdersTable.js
import React, { useEffect, useState } from "react";
import userServices from "services/userServices";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../components/Pagination";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import DropdownFilter from "utils/DropdownFilter";
import { useNotification } from "providers/NotificationProvider";
import {
  Card,
  Button,
  Row,
  CardBody,
  CardTitle,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from "reactstrap";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import helpers from "utils/helpers";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "context/TranslationProvider";
import useNotificationHighlight from "hooks/useNotificationHighlight";
import PickupConfirmationModal from "modals/PickupConfirmationModal";

function OrdersTable({
  orders,
  setOrders,
  pagination: paginationData,
  tableState,
  setTableState,
  onTableChange,
  deleteRow,
  handleOpenUserDetails,
  isItemsLoading,
  statusLoadingId
}) {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const [updatingStatus, setUpdatingStatus] = useState({});
  const { filters } = tableState || {};
  const { status: statusFilter = "All", dateRange: dateFilter = "All" } = filters || {};
  const { permission } = useAuth();
  const { notify } = useNotification();
  const orderPermission = permission?.permissions?.modules?.orders;

  const [showPickupModal, setShowPickupModal] = useState(false);
  const [pickupModalOrder, setPickupModalOrder] = useState(null);
  
  const [overview, setOverview] = useState({
    active: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });

  const [localSearch, setLocalSearch] = useState(tableState.search || "");

  // Debounce search input - resetting page to 1
  useEffect(() => {
    // Only update if search has actually changed or if it was cleared
    if (localSearch === tableState.search && localSearch !== "") return;
    if (localSearch === "" && tableState.search === "") return;

    const handler = setTimeout(() => {
      setTableState((prev) => ({
        ...prev,
        search: localSearch,
        page: 1,
      }));
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, tableState.search, setTableState]);

  const handleFilterChange = (filterName, value) => {
    setTableState((prev) => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        [filterName]: value,
      },
    }));
  };

  const handleClearFilters = () => {
    setLocalSearch("");
    setTableState((prev) => ({
      ...prev,
      search: "",
      page: 1,
      filters: {
        ...prev.filters,
        status: "All",
        dateRange: "All",
      },
    }));
  };

  useEffect(() => {
    fetchOverview();
  }, [paginationData?.total]);

  const fetchOverview = async () => {
    try {
      const res = await userServices.adminOverview();
      const ordersCount = res?.data?.Orders || res?.count?.Orders || res?.Orders || {};

      setOverview({
        active: ordersCount.activeDeliveries ?? (ordersCount.pickedUp || 0),
        pending: ordersCount.pendingDeliveries ?? ((ordersCount.confirmed || 0) + (ordersCount.readyForPickup || 0) + (ordersCount.pending || 0)),
        completed: ordersCount.completedDeliveries ?? (ordersCount.delivered || 0),
        cancelled: ordersCount.cancelledDeliveries ?? ((ordersCount.cancelled || 0) + (ordersCount.failed || 0)),
      });
    } catch (err) {
      console.error("Failed to fetch order overview", err);
    }
  };

  const goToOrderDetails = (item) => {
    if (!item?._id) return;
    navigate(`/admin/all-orders/orderDetails/${item._id}`);
  };

  const handleStatusSelect = (order, targetStatus) => {
    if (targetStatus === "Picked Up") {
      setPickupModalOrder(order);
      setShowPickupModal(true);
    } else {
      handleOrderStatusChange(order, targetStatus);
    }
  };

  const handleConfirmPickupSubmit = async ({ expectedArrivalDate, expectedArrivalTime }) => {
    if (!pickupModalOrder?._id) return;
    try {
      const result = await userServices.confirmPickup(pickupModalOrder._id, {
        expectedArrivalDate,
        expectedArrivalTime,
      });
      if (result?.success || result?.data) {
        notify("success", t("page.orders.toast.pickupConfirmed", "Pickup confirmed successfully."));
        if (setOrders) {
          setOrders(prev => prev.map(o => o._id === pickupModalOrder._id ? { ...o, status: "Picked Up" } : o));
        }
        setShowPickupModal(false);
        setPickupModalOrder(null);
        onTableChange("refresh", {});
      } else {
        const msg = result?.message || result?.error || "Failed to confirm pickup";
        notify("error", t(result?.messageKey || "page.orders.toast.pickupFailed", msg));
        throw new Error(msg);
      }
    } catch (error) {
      const errPayload = error?.response?.data || {};
      const msg = errPayload?.message || errPayload?.error || error?.message || "Failed to confirm pickup";
      notify("error", t(errPayload?.messageKey || "page.orders.toast.pickupFailed", msg));
      throw error;
    }
  };

  const handleOrderStatusChange = async (order, status) => {
    const orderId = order._id;
    if (updatingStatus[orderId]) return;
    setUpdatingStatus((prev) => ({ ...prev, [orderId]: true }));
    try {
      const result = await userServices.updateOrderStatus(orderId, status);
      const payload = result?.data || result;
      if (payload?.success === true) {
        notify("success", t(payload?.messageKey || "page.orders.toast.statusUpdated", payload?.message || t("page.orders.toast.statusUpdated", "Order status updated successfully.")));
        
        // Instant local update
        if (setOrders) {
          setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status: status } : o));
        }

        onTableChange("refresh", {});
      } else {
        const errMsgKey = payload?.messageKey || payload?.error || "page.orders.toast.statusUpdateFailed";
        const msg = errMsgKey === "auth.errors.invalidOrderStatusTransition" && payload?.message 
          ? payload.message 
          : t(errMsgKey, payload?.message || t("page.orders.toast.statusUpdateFailed", "Failed to update status."));
        notify("error", msg);
      }
    } catch (error) {
      const errPayload = error?.response?.data || {};
      const errMsgKey = errPayload?.messageKey || errPayload?.error || "page.orders.toast.statusUpdateFailed";
      const msg = errMsgKey === "auth.errors.invalidOrderStatusTransition" && errPayload?.message 
        ? errPayload.message 
        : t(errMsgKey, errPayload?.message || t("page.orders.toast.statusUpdateFailed", "Failed to update status."));
      notify("error", msg);
    } finally {
      setUpdatingStatus((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }
  };

  const isRowIncomplete = (row) => {
    const fieldsToCheck = [
      row.sender_listing_id?.userId?.firstName || row.traveler_listing_id?.userId?.firstName,
      row.sender_listing_id?.fromPlace?.name || row.traveler_listing_id?.fromPlace?.name,
      row.sender_listing_id?.toPlace?.name || row.traveler_listing_id?.toPlace?.name,
      row.sender_listing_id?.date || row.traveler_listing_id?.date,
      row.orderid,
    ];

    return fieldsToCheck.some((field) => !field || field === "");
  };

  const columns = [
    {
      dataField: "orderid",
      order: "desc",
      text: t("table.headers.orderId", "Order ID"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell, row) => {
        const incomplete = isRowIncomplete(row);

        return (
          <>
            <span
              id={`orderid-${row._id}`}
              className={`font-weight-bold sidenav-toggler ${
                incomplete ? "text-muted" : "text-primary"
              }`}
              onClick={() => {
                if (!incomplete) {
                  goToOrderDetails(row);
                }
              }}
            >
              {cell}
            </span>

            <UncontrolledTooltip
              target={`orderid-${row._id}`}
              key={`tooltip-orderid-${row._id}-${language}`}
            >
              {incomplete
                ? t("tooltip.incomplete", "Order incomplete")
                : t("tooltip.viewOrder", "View order details")}
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      dataField: "sender",
      text: t("page.orders.sender", "Sender"),
      headerAlign: "left",
      align: "left",
      sort: false,
      formatter: (_, row) => {
        const incomplete = isRowIncomplete(row);

        const user = row.sender_listing_id?.userId;
        const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

        if (!name) return t("common.empty", "Empty");

        return (
          <>
            <span
              id={`sender-${row._id}`}
              className={`${!incomplete ? "text-primary cursor-pointer font-weight-bold" : ""}`}
              onClick={() => {
                if (!incomplete && handleOpenUserDetails) {
                  handleOpenUserDetails(row._id, "sender");
                }
              }}
            >
              {name}
            </span>

            {!incomplete && (
              <UncontrolledTooltip
                target={`sender-${row._id}`}
                key={`tooltip-sender-${row._id}-${language}`}
              >
                {t("tooltip.viewUserProfile", "View user profile")}
              </UncontrolledTooltip>
            )}
          </>
        );
      }
    },
    {
      dataField: "traveler",
      text: t("page.orders.traveler", "Traveler"),
      headerAlign: "left",
      align: "left",
      sort: false,
      formatter: (_, row) => {
        const incomplete = isRowIncomplete(row);

        const user = row.traveler_listing_id?.userId;
        const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

        if (!name) return t("common.empty", "Empty");

        return (
          <>
            <span
              id={`traveler-${row._id}`}
              className={`${!incomplete ? "text-primary cursor-pointer font-weight-bold" : ""}`}
              onClick={() => {
                if (!incomplete && handleOpenUserDetails) {
                  handleOpenUserDetails(row._id, "traveler");
                }
              }}
            >
              {name}
            </span>

            {!incomplete && (
              <UncontrolledTooltip
                target={`traveler-${row._id}`}
                key={`tooltip-traveler-${row._id}-${language}`}
              >
                {t("tooltip.viewUserProfile", "View user profile")}
              </UncontrolledTooltip>
            )}
          </>
        );
      }
    },
    {
      dataField: "fromTo",
      text: t("table.headers.location", "Location"),
      headerAlign: "left",
      align: "left",
      sort: false,
      formatter: (_, row) => {
        const from = row.sender_listing_id?.fromPlace?.name || row.traveler_listing_id?.fromPlace?.name || t("common.empty", "Empty");
        const to = row.sender_listing_id?.toPlace?.name || row.traveler_listing_id?.toPlace?.name || t("common.empty", "Empty");
        return `${from} \u2192 ${to}`;
      },
    },
    {
      dataField: "status",
      text: t("table.headers.status", "Status"),
      sort: false,
      headerAlign: "left",
      align: "left",
      formatter: (_, row, rowIndex, formatExtraData) => {
        const { updatingStatus, statusLoadingId } = formatExtraData || {};
        const currentStatus = helpers.normalizeOrderStatus(row.status);
        const statusClass = helpers.getStatusClass(currentStatus, "order");
        const canUpdateOrder = orderPermission?.update;
        const incomplete = isRowIncomplete(row);
        const isDeleted = row.deleted === true;
        const isUpdating = !!updatingStatus?.[row._id];
        const disabled = !canUpdateOrder || incomplete || isUpdating || isDeleted || statusLoadingId === row._id;

        return (
          <>
            <UncontrolledDropdown>
              <DropdownToggle
                caret
                size="sm"
                className={`status-dropdown ${statusClass} ${disabled ? "cursor-not-allowed opacity-75" : ""}`}
                disabled={disabled}
                id={`status-${row._id}`}
              >
                {t(`page.orders.status.${currentStatus}`, helpers.ORDER_STATUS_API_MAP[currentStatus] || currentStatus)}
              </DropdownToggle>
              <DropdownMenu>
                {Object.keys(helpers.ORDER_STATUSES).map((status) => (
                  <DropdownItem
                    key={status}
                    disabled={status === currentStatus || disabled}
                    onClick={() =>
                      !disabled &&
                      handleStatusSelect(row, helpers.ORDER_STATUS_API_MAP[status])
                    }
                  >
                    {t(`page.orders.status.${status}`, helpers.ORDER_STATUS_API_MAP[status] || status)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            {!isUpdating && statusLoadingId !== row._id && (
              <UncontrolledTooltip target={`status-${row._id}`} key={`tooltip-status-${row._id}-${language}`}>
                {incomplete
                  ? t("tooltip.incomplete", "Order incomplete")
                  : isDeleted
                  ? t("page.orders.buttons.orderDeleted", "Deleted order")
                  : !canUpdateOrder
                  ? t("tooltip.noPermission", "Permission denied")
                  : t("modal.orders.updateStatusTitle.default", "Are you sure you want to change the delivery status?")}
              </UncontrolledTooltip>
            )}
          </>
        );
      },
      formatExtraData: { updatingStatus, statusLoadingId },
    },
    {
      dataField: "journey_date",
      text: t("page.orders.journeyDate", "Journey Date"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (_, row) => {
        const acceptedBy = ["sender", "traveler"].includes(row.order_accepted_by) ? row.order_accepted_by : null;
        const date = row.acceptedOffer?.departureAt || row.acceptedOffer?.offerDetails?.departureAt || (acceptedBy === "sender" ? (row.sender_listing_id?.date || row.traveler_listing_id?.date) : (row.traveler_listing_id?.date || row.sender_listing_id?.date));
        return date ? helpers.getFormattedDate(date, t) : t("common.empty", "Empty");
      },
    },
    {
      dataField: "actions",
      text: t("table.headers.actions", "Actions"),
      headerAlign: "center",
      align: "left",
      sort: false,
      formatter: (_, row, rowIndex, formatExtraData) => {
        const { statusLoadingId } = formatExtraData || {};
        const isDeleted = row?.deleted === true;
        const incomplete = isRowIncomplete(row);
        const disabled = !orderPermission?.delete || isDeleted || incomplete || statusLoadingId === row._id;

        return (
          <div className="d-flex align-items-center gap-1">
            <Button
              id={`delete-${row._id}`}
              className="btn btn-sm"
              disabled={disabled}
              onClick={() => !disabled && deleteRow(row)}
            >
              <DeleteIcon fontSize="small" color="error" />
            </Button>
            <UncontrolledTooltip target={`delete-${row._id}`} key={`tooltip-delete-${row._id}-${language}`}>
              {!orderPermission?.delete
                ? t("tooltip.deleteNotAllowed", "Delete not allowed")
                : isDeleted
                ? t("page.orders.buttons.orderDeleted", "Deleted order")
                : incomplete
                ? t("tooltip.incomplete", "Order incomplete")
                : t("page.orders.buttons.orderDelete", "Delete order")}
            </UncontrolledTooltip>
          </div>
        );
      },
      formatExtraData: { statusLoadingId },
    },
  ];

  const overviewCards = [
    { key: "active", value: overview.active, icon: "ni ni-delivery-fast", bg: "bg-gradient-info" },
    { key: "pending", value: overview.pending, icon: "ni ni-time-alarm", bg: "bg-gradient-warning" },
    { key: "completed", value: overview.completed, icon: "ni ni-check-bold", bg: "bg-gradient-success" },
    { key: "cancelled", value: overview.cancelled, icon: "ni ni-fat-remove", bg: "bg-gradient-danger" },
  ];

  return (
    <>
      <Row>
        {overviewCards.map((card, idx) => (
          <Col md="6" xl="3" key={idx}>
            <Card className="card-stats shadow">
              <CardBody>
                <Row>
                  <div className="col">
                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0 card-title">
                      {t(`table.cards.${card.key}.line1`)}
                      <br />
                      {t(`table.cards.${card.key}.line2`)}
                    </CardTitle>
                    <span className="h2 font-weight-bold mb-0">{card.value}</span>
                  </div>
                  <Col className="col-auto">
                    <div className={`icon icon-shape ${card.bg} text-white rounded-circle shadow`}>
                      <i className={card.icon} />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
      <Row>
        <div className="col">
          <Card className="shadow">
            <ToolkitProvider keyField="_id" data={orders} columns={columns}>
              {(props) => (
                <div className="py-4 w-full flex flex-col">
                  <div
                    id="datatable-basic_filter"
                    className="dataTables_filter px-4 pb-3 d-flex align-items-center justify-content-between w-100 flex-wrap gap-2"
                  >
                    <label className="d-flex align-items-center gap-2 mb-0">
                      <span className="text-sm font-medium text-slate-600">{t("table.search", "Search")}:</span>
                      <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                        <input
                          className="form-control form-control-sm border-2"
                          placeholder={t("table.search", "Search")}
                          type="text"
                          value={localSearch || ""}
                          onChange={(e) => setLocalSearch(e.target.value)}
                          style={{ paddingRight: "24px" }}
                        />
                        {localSearch && (
                          <span
                            onClick={() => {
                              setLocalSearch("");
                              setTableState((prev) => ({
                                ...prev,
                                search: "",
                                page: 1,
                              }));
                            }}
                            className="position-absolute cursor-pointer search-clear-btn"
                          >
                            <CloseIcon style={{ fontSize: "14px" }} />
                          </span>
                        )}
                      </div>
                    </label>

                    <div className="listing-header-right d-flex align-items-center">
                      <span className="text-sm font-weight-600 text-muted mr-2">
                        {t("table.filterBy", "Filter By")}
                      </span>
                      <DropdownFilter
                        value={statusFilter}
                        onChange={(val) => handleFilterChange("status", val)}
                        options={helpers.getFilter("ORDER_STATUS_FILTER", t)}
                      />

                      <DropdownFilter
                        value={dateFilter}
                        onChange={(val) => handleFilterChange("dateRange", val)}
                        options={helpers.getFilter("ORDER_DATE_FILTER", t)}
                      />

                      <Button
                        size="sm"
                        className="ml-2 btn-neutral text-danger font-weight-600"
                        onClick={handleClearFilters}
                      >
                        {t("common.clearFilters", "Clear Filters")}
                      </Button>
                    </div>
                  </div>

                  <div className="table-responsive max-h-[60vh] overflow-y-auto min-h-[300px]">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      rowClasses={(row) =>
                        isHighlighted("order", row._id) || isHighlighted("order", row.orderid)
                          ? "bg-[#F0F7FF] border-left-highlight"
                          : ""
                      }
                      remote={{ pagination: true, sort: true }}
                      onTableChange={onTableChange}
                      defaultSorted={[{
                        dataField: tableState?.sortBy || "orderid",
                        order: tableState?.sortOrder === 1 ? "asc" : "desc",
                      }]}
                      noDataIndication={() => (
                        <div className="text-center text-slate-500 py-4 w-full">
                           {isItemsLoading ? t("common.loading", "Loading....") : t("page.orders.noOrders", "No Orders Found")}
                        </div>
                      )}
                      bordered={false}
                    />
                  </div>
                  <Pagination
                    totalRows={paginationData?.total || 0}
                    rowsPerPage={tableState?.limit || 20}
                    currentPage={(tableState?.page || 1) - 1}
                    onPageChange={(page) => setTableState((prev) => ({ ...prev, page: page + 1 }))}
                    onRowsPerPageChange={(size) => setTableState((prev) => ({ ...prev, limit: size, page: 1 }))}
                    t={t}
                  />
                </div>
              )}
            </ToolkitProvider>
          </Card>
        </div>
      </Row>
      <PickupConfirmationModal
        open={showPickupModal}
        order={pickupModalOrder}
        destinationTimezone={pickupModalOrder?.traveler_listing_id?.toPlace?.name || pickupModalOrder?.sender_listing_id?.toPlace?.name || ""}
        onClose={() => {
          setShowPickupModal(false);
          setPickupModalOrder(null);
        }}
        onConfirm={handleConfirmPickupSubmit}
      />
    </>
  );
}

export default OrdersTable;